import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import LaunchIcon from '@mui/icons-material/Launch';
import { get, post } from "src/utils/networkUtils";

const PREVIEW_URL = (id, offset, limit) =>
  `/admin/weekly-gameweek-prize/gameweeks/${id}/preview?offset=${offset}&limit=${limit}`;
const RESOLVE_URL = (id) => `/admin/weekly-gameweek-prize/gameweeks/${id}/resolve`;
const RESULT_URL = (id) => `/admin/weekly-gameweek-prize/gameweeks/${id}/result`;
const LIST_URL = `/admin/weekly-gameweek-prize/gameweeks`;
const PAGE_SIZE = 50;

const TIEBREAK_LABELS = [
  "No tie — clear winner",
  "Goals scored",
  "Assists",
  "Earliest team submitted",
  "Random selection",
];

export default function WeeklyGameweekPrizesPage() {
  const navigate = useNavigate()

  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGwId, setSelectedGwId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [offset, setOffset] = useState(0);

  const loadGameweeks = useCallback(() => {
    setLoading(true);
    setLoadError("");
    get(LIST_URL)
      .then((r) => {
        const data = r?.data ?? [];
        setGameweeks(data);
        if (data.length > 0 && !selectedGwId) {
          setSelectedGwId(data[0].gameweek_id);
        }
      })
      .catch((err) => {
        setLoadError(
          err?.response?.data?.message ?? err?.message ?? "Failed to load resolved prizes."
        );
      })
      .finally(() => setLoading(false));
  }, [selectedGwId]);

  const selectedGw = gameweeks.find((g) => g.gameweek_id === selectedGwId);

  const loadPreview = useCallback(() => {
    if (!selectedGwId) return;
    setPreview(null);
    setResult(null);
    setLoadError("");
    setOffset(0);

    // Only call result endpoint if we know it's resolved (avoids 404 toast)
    if (selectedGw?.resolved) {
      get(RESULT_URL(selectedGwId))
        .then((r) => {
          if (r?.data) {
            setResult(r.data);
          }
        })
        .catch((err) => {
          setLoadError(
            err?.response?.data?.message ?? err?.message ?? "Failed to load result."
          );
        });
    } else {
      get(PREVIEW_URL(selectedGwId, 0, PAGE_SIZE))
        .then((r) => {
          setPreview(r?.data ?? null);
        })
        .catch((err) => {
          setLoadError(
            err?.response?.data?.message ?? err?.message ?? "Failed to load preview."
          );
        });
    }
  }, [selectedGwId, selectedGw?.resolved]);

  const handleNextPage = async () => {
    const nextOffset = offset + PAGE_SIZE;
    try {
      const r = await get(PREVIEW_URL(selectedGwId, nextOffset, PAGE_SIZE));
      if (r?.data) {
        setPreview(r.data);
        setOffset(nextOffset);
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ?? err?.message ?? "Failed to load page."
      );
    }
  };

  const handlePrevPage = async () => {
    const prevOffset = Math.max(0, offset - PAGE_SIZE);
    try {
      const r = await get(PREVIEW_URL(selectedGwId, prevOffset, PAGE_SIZE));
      if (r?.data) {
        setPreview(r.data);
        setOffset(prevOffset);
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ?? err?.message ?? "Failed to load page."
      );
    }
  };

  useEffect(() => {
    loadGameweeks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleConfirmResolve = async () => {
    setRunError("");
    setRunning(true);
    try {
      const r = await post(RESOLVE_URL(selectedGwId));
      const data = r?.data;
      if (!data?.winner_user_id) {
        setRunError("Prize resolution did not return a winner.");
        return;
      }
      setResult(data);
      setPreview(null);
      setConfirmOpen(false);
      loadGameweeks();
    } catch (err) {
      setRunError(
        err?.response?.data?.message ?? err?.message ?? "Failed to resolve prize."
      );
    } finally {
      setRunning(false);
    }
  };

  const selectedGwNumber = gameweeks.find((g) => g.gameweek_id === selectedGwId)?.gameweek_number;

  return (
    <>
      <Helmet>
        <title>Weekly Prizes</title>
      </Helmet>

      <Container maxWidth="lg">
        <Typography variant="h4" mb={1}>
          Weekly Gameweek Prizes
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Resolve £250 weekly prizes for GW1–GW37. Eligibility: no booster used, team entered early.
        </Typography>

        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : loadError && !preview && !result ? (
          <Alert severity="warning">{loadError}</Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Gameweeks
              </Typography>
              <Box sx={{ maxHeight: 560, overflowY: "auto", pr: 1 }}>
                <Stack spacing={1}>
                  {gameweeks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No gameweeks found.
                    </Typography>
                  ) : (
                    gameweeks.map((g) => (
                    <Paper
                      key={g.gameweek_id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        cursor: "pointer",
                        borderColor:
                          g.gameweek_id === selectedGwId
                            ? "primary.main"
                            : "divider",
                        bgcolor:
                          g.gameweek_id === selectedGwId
                            ? "action.selected"
                            : "background.paper",
                      }}
                      onClick={() => setSelectedGwId(g.gameweek_id)}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" fontWeight={600}>
                          GW{g.gameweek_number}
                        </Typography>
                        {g.resolved ? (
                          <Chip size="small" label="Resolved" color="success" />
                        ) : g.status === "finalised" ? (
                          <Chip size="small" label="Pending" color="warning" />
                        ) : (
                          <Chip size="small" label="In Progress" color="info" />
                        )}
                      </Stack>
                      {g.resolved && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {g.winner_username || g.winner_ref_id || "Winner recorded"}
                        </Typography>
                      )}
                    </Paper>
                  ))
                )}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              {result ? (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    GW{result.gameweek_number} — Winner
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Team / Manager
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {result.winner_team_name}
                      </Typography>
                      <Typography variant="body2">
                        {result.winner_username || result.winner_ref_id}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Gameweek Points
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {result.gameweek_points}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Goals
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {result.goals_scored}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Assists
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {result.assists}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Tie-breaker
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {TIEBREAK_LABELS[result.tie_broken_at_step] ?? "Unknown"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Eligible entrants
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {result.eligible_count}
                        </Typography>
                      </Box>
                    </Stack>
                    {result.seed && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Random seed
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {result.seed}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ) : preview ? (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    GW{preview.gameweek_number} — Preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {preview.already_resolved
                      ? "Already resolved."
                      : preview.can_resolve
                      ? "Ready to resolve."
                      : "Cannot resolve yet."}
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">
                      Teams (rank {offset + 1}–{offset + (preview?.teams?.length ?? 0)})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        disabled={offset === 0}
                        onClick={handlePrevPage}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        disabled={preview?.teams?.length < PAGE_SIZE}
                        onClick={handleNextPage}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>Team</TableCell>
                          <TableCell>Manager</TableCell>
                          <TableCell align="right">GW PTS</TableCell>
                          <TableCell align="center">Booster</TableCell>
                          <TableCell align="center">Eligible</TableCell>
                          <TableCell align="right">Goals</TableCell>
                          <TableCell align="right">Assists</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.teams.map((t, idx) => (
                          <TableRow
                            key={t.fantasy_team_id}
                            sx={{
                              bgcolor: t.eligible ? "success.lighter" : undefined,
                            }}
                          >
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{t.team_name}</TableCell>
                            <TableCell style={{ display: "flex", alignItems: "center" }}>
                              <span>{t.user_display_name}</span>
                              <LaunchIcon style={{height: "16px", width: "16px", marginLeft: "8px"}} onClick={() => {
                                navigate(`/dashboard/users/${t.user_id}`)
                              }}/>
                            </TableCell>
                            <TableCell align="right">
                              {t.gameweek_points}
                            </TableCell>
                            <TableCell align="center">
                              {t.booster_used ? (
                                <Chip size="small" label="Yes" color="error" />
                              ) : (
                                <Chip size="small" label="No" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {t.eligible ? (
                                <Chip size="small" label="Yes" color="success" />
                              ) : (
                                <Chip size="small" label="No" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">{t.goals_scored}</TableCell>
                            <TableCell align="right">{t.assists}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Button
                      variant="contained"
                      disabled={!preview.can_resolve || running}
                      onClick={() => {
                        setRunError("");
                        setConfirmOpen(true);
                      }}
                    >
                      Resolve Prize
                    </Button>
                    {!preview.can_resolve && !preview.already_resolved && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedGw?.status !== "finalised"
                          ? "Gameweek not finalised."
                          : "Resolve will validate eligibility."}
                      </Typography>
                    )}
                  </Stack>

                  {runError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {runError}
                    </Alert>
                  )}
                </Paper>
              ) : (
                <Alert severity="info">
                  Select a gameweek from the list to preview or view the resolved winner.
                </Alert>
              )}
            </Grid>
          </Grid>
        )}
      </Container>

      <Dialog open={confirmOpen} onClose={() => !running && setConfirmOpen(false)}>
        <DialogTitle>Resolve weekly prize?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will determine the £250 winner for{" "}
            <strong>GW{preview?.gameweek_number ?? selectedGwNumber}</strong> using the eligibility rules
            and tie-breakers. The result cannot be undone.
          </DialogContentText>
          {runError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {runError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmResolve} disabled={running}>
            {running ? "Resolving…" : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
